import React from "react";
import { Check } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import Table, { TableColumn } from "@/shared/components/ui/table";
import { Progress } from "@/shared/components/ui/progress";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Button } from "@/shared/components/ui/button";
import { AnimatedShinyText } from "@/shared/components/ui/animated-shiny-text";
import { DocumentBadge } from "@/shared/components/DocumentBadge";
import { Tag, type TagColor } from "@/shared/components/ui/tag";
import { SecurityNote } from "@/shared/components/ui/security-note";

const TAG_COLORS: TagColor[] = [
  "green",
  "blue",
  "yellow",
  "stone",
  "salmon",
  "orange",
  "purple",
  "brand",
];

const SAMPLE_SOURCE_DOCUMENT = {
  id: 1,
  file_name: "death-certificate.pdf",
  display_name: "Death Certificate",
  created_at: new Date().toISOString(),
};

const DataDisplayPage: React.FC = () => {
  const [progressValue, setProgressValue] = React.useState(33);

  const tableColumns: TableColumn<{ name: string; email: string; role: string }>[] = [
    { header: "Name", accessor: "name" },
    { header: "Email", accessor: "email" },
    { header: "Role", accessor: "role" },
  ];

  const tableData = [
    { name: "John Doe", email: "john@example.com", role: "Admin" },
    { name: "Jane Smith", email: "jane@example.com", role: "User" },
    { name: "Bob Johnson", email: "bob@example.com", role: "User" },
  ];

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">
          Data Display
        </h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Components for presenting data and content to users.
        </p>
      </header>

      <div className="space-y-16">
        {/* Animated Shiny Text */}
        <section id="animated-shiny-text" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Animated Shiny Text</h2>
            <p className="mt-1 text-sm text-stone-500">
              Decorative text with a shimmer animation effect.
            </p>
          </div>
          <div className="space-y-4">
            <div className="rounded-lg border bg-stone-50 p-6">
              <p className="text-lg font-medium">
                <AnimatedShinyText>This text has a shiny effect</AnimatedShinyText>
              </p>
            </div>
            <div className="rounded-lg border bg-stone-900 p-6">
              <p className="text-lg font-medium text-white">
                <AnimatedShinyText>Works on dark backgrounds too</AnimatedShinyText>
              </p>
            </div>
          </div>
        </section>

        {/* Badge */}
        <section id="badge" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Badge</h2>
            <p className="mt-1 text-sm text-stone-500">
              Small labels for status, categories, or counts.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="stone">Stone</Badge>
          </div>
        </section>

        {/* Card */}
        <section id="card" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Card</h2>
            <p className="mt-1 text-sm text-stone-500">
              Container for grouping related content with optional header and footer.
            </p>
          </div>
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Card Title</CardTitle>
              <CardDescription>Card description goes here</CardDescription>
            </CardHeader>
            <CardContent>
              <p>This is the card content area.</p>
            </CardContent>
            <CardFooter>
              <Button>Action</Button>
            </CardFooter>
          </Card>
        </section>

        {/* Progress */}
        <section id="progress" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Progress</h2>
            <p className="mt-1 text-sm text-stone-500">
              Visual indicator for task completion status.
            </p>
          </div>
          <div className="w-full space-y-3">
            <Progress value={progressValue} />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProgressValue(Math.max(0, progressValue - 25))}
              >
                Decrease
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProgressValue(Math.min(100, progressValue + 25))}
              >
                Increase
              </Button>
            </div>
            <p className="text-sm text-stone-500">Current value: {progressValue}%</p>
          </div>
        </section>

        {/* Security Note */}
        <section id="security-note" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Security Note</h2>
            <p className="mt-1 text-sm text-stone-500">
              Reassurance line about encryption, privacy, or where data is stored. Use below the
              primary action of a form, upload, or checkout step.
            </p>
          </div>
          <div className="space-y-3 rounded-lg border p-6">
            <SecurityNote size="sm" text="Small — files are encrypted and stored securely" />
            <SecurityNote size="md" text="Medium — files are encrypted and stored securely" />
            <SecurityNote size="lg" text="Large — files are encrypted and stored securely" />
          </div>
        </section>

        {/* Skeleton */}
        <section id="skeleton" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Skeleton</h2>
            <p className="mt-1 text-sm text-stone-500">Loading placeholders for content states.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-[250px]" />
              <Skeleton className="h-4 w-[200px]" />
              <Skeleton className="h-4 w-[150px]" />
            </div>
            <div className="flex items-center space-x-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
              </div>
            </div>
          </div>
        </section>

        {/* Table */}
        <section id="table" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Table</h2>
            <p className="mt-1 text-sm text-stone-500">
              Display tabular data with sortable columns and pagination support.
            </p>
          </div>
          <Table columns={tableColumns} data={tableData} className="rounded-md border" />
        </section>

        {/* Document Badge */}
        <section id="document-badge" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Document Badge</h2>
            <p className="mt-1 text-sm text-stone-500">
              Tag marking a value auto-extracted from a document. Opens the source document on
              click, and shows its name in a tooltip.
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-base font-medium">Variants</h3>
              <div className="flex flex-wrap items-center gap-3">
                <DocumentBadge
                  sourceDocument={SAMPLE_SOURCE_DOCUMENT}
                  variant="short"
                  onClick={() => {}}
                />
                <DocumentBadge
                  sourceDocument={SAMPLE_SOURCE_DOCUMENT}
                  variant="long"
                  onClick={() => {}}
                />
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-medium">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <DocumentBadge
                  sourceDocument={SAMPLE_SOURCE_DOCUMENT}
                  variant="short"
                  onClick={() => {}}
                  size="sm"
                />
                <DocumentBadge
                  sourceDocument={SAMPLE_SOURCE_DOCUMENT}
                  variant="short"
                  onClick={() => {}}
                  size="md"
                />
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-medium">Without Shimmer</h3>
              <div className="flex flex-wrap items-center gap-3">
                <DocumentBadge
                  sourceDocument={SAMPLE_SOURCE_DOCUMENT}
                  variant="long"
                  onClick={() => {}}
                  color="stone"
                  size="sm"
                  shimmer={false}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Tag */}
        <section id="tag" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Tag</h2>
            <p className="mt-1 text-sm text-stone-500">
              Compact pill for labels, categories, and metadata.
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-base font-medium">Colors</h3>
              <div className="flex flex-wrap gap-3">
                {TAG_COLORS.map((color) => (
                  <Tag key={color} color={color}>
                    {color}
                  </Tag>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-medium">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Tag size="sm">Small</Tag>
                <Tag size="md">Medium</Tag>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-medium">With Icon</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Tag size="sm" icon={Check}>
                  Small
                </Tag>
                <Tag size="md" icon={Check}>
                  Medium
                </Tag>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DataDisplayPage;
